import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { useNavigate } from "react-router";
import { makeStyles, mergeClasses, shorthands, Spinner } from "@fluentui/react-components";
import { vorgangService, VorgangDto, TodoDto } from "../services/vorgangService";
import { openVorgangCreateFlow } from "./vorgang/create/VorgangCreateFlowWindow";
import { useWindowManager } from "./windowManager/WindowManagerContext";
import { TodoListRow, type TodoStatusValue } from "./todo/TodoListRow";
import { useAuth } from "../context/AuthContext";
import { useUserLabels } from "../hooks/useUserLabels";
import { bearbeiterKeys } from "../utils/vorgangScope";
import { deriveVorgangStatus } from "./vorgang/shared/vorgangStatus";
import { findUserBy } from "../services/userService";
import { fmtDate, fmtTwv, initialsFor, participantIds, viaCompanyLabel } from "./contactVorgaengeHelpers";
import { apiUrl } from "../utils/api";

/**
 * Steuerung von außen, wenn die Liste eingebettet läuft und ihre Aktionen im
 * Kopf der umgebenden Sektion sitzen (siehe {@link ContactKundentagebuchTab}).
 */
export interface ContactVorgaengeHandle {
    /** Neu laden (Knopf „↻" im Sektionskopf). */
    reload: () => void;
    /** Öffnet den Anlege-Flow für einen neuen Vorgang. */
    openCreate: () => void;
}

interface Props {
    contactId: number;
    /**
     * `"vorgaenge"` (Default): eigenständiger Bereich mit Kopfzeile
     * (Titel, Zähler, „↻", „+ Neuer Vorgang") und eigenem Scrollbereich — so
     * nutzt ihn der Vorgänge-Reiter der Kontaktdetails und die
     * Immobilienvertrag-Detailseite ({@link ImmobilienContractDetailPage}).
     *
     * `"embedded"`: **ohne** eigene Kopfzeile und ohne eigenen Scrollbereich —
     * Titel, Zähler und Aktionen liefert die umgebende Klapp-Sektion, gescrollt
     * wird die Seite. Für den Kundentagebuch-Reiter.
     */
    variant?: "vorgaenge" | "embedded";
    /** Meldet die Anzahl nach oben (Zähler im Sektionskopf). */
    onCountChange?: (count: number) => void;
}

const MAX_AVATARS = 4;

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#ffffff",
        fontFamily: "'Barlow', 'Segoe UI', sans-serif",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ...shorthands.padding("12px", "20px"),
        backgroundColor: "#faf6fb",
        ...shorthands.borderBottom("1px", "solid", "#e3e0e7"),
    },
    title: {
        fontWeight: 600,
        fontSize: "1rem",
        margin: 0,
    },
    countBadge: {
        display: "inline-block",
        ...shorthands.padding("2px", "8px"),
        marginLeft: "8px",
        fontSize: "11px",
        backgroundColor: "#e3e0e7",
        borderRadius: "999px",
        color: "#2d142b",
    },
    actionsRow: {
        display: "flex",
        columnGap: "8px",
    },
    iconBtn: {
        ...shorthands.padding("6px", "10px"),
        borderRadius: "999px",
        ...shorthands.border("1px", "solid", "#e3e0e7"),
        backgroundColor: "#ffffff",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "12px",
        ":hover": { backgroundColor: "#f5eef6" },
    },
    primaryBtn: {
        ...shorthands.padding("8px", "16px"),
        borderRadius: "999px",
        ...shorthands.border("1px", "solid", "#4d2051"),
        backgroundColor: "#4d2051",
        color: "#ffffff",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "12px",
        fontWeight: 700,
        ":hover": { backgroundColor: "#3a163d" },
    },
    body: {
        flex: 1,
        overflowY: "auto",
    },
    // Eingebettet: kein eigener Scrollbereich und keine feste Höhe — sonst
    // entsteht eine Scroll-Insel in der Klapp-Sektion (gescrollt wird <main>).
    rootEmbedded: {
        height: "auto",
        backgroundColor: "transparent",
    },
    bodyEmbedded: {
        flexGrow: 0,
        overflowY: "visible",
    },
    section: {
        ...shorthands.margin("10px", "12px"),
        ...shorthands.border("1px", "solid", "#e3e0e7"),
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
    },
    sectionHeader: {
        ...shorthands.padding("8px", "16px"),
        backgroundColor: "#f7f5fa",
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "#6f6c75",
        fontWeight: 600,
    },
    vorgangRow: {
        display: "grid",
        gridTemplateColumns: "28px minmax(0, 1fr) 150px 130px auto",
        alignItems: "center",
        columnGap: "12px",
        ...shorthands.padding("12px", "20px"),
        backgroundColor: "#ffffff",
        cursor: "pointer",
        userSelect: "none",
        ":hover": { backgroundColor: "#faf6fb" },
    },
    chevronBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "24px",
        height: "24px",
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        color: "#6f6c75",
        cursor: "pointer",
        borderRadius: "6px",
        fontSize: "14px",
        lineHeight: "1",
        ":hover": { backgroundColor: "#efe7f1" },
    },
    main: {
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        rowGap: "3px",
    },
    titleLine: {
        display: "flex",
        alignItems: "center",
        columnGap: "8px",
        minWidth: 0,
    },
    titelText: {
        fontWeight: 600,
        fontSize: "14px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    subLine: {
        fontSize: "12px",
        color: "#6f6c75",
    },
    badgeMention: {
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.padding("1px", "8px"),
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 700,
        backgroundColor: "#fdf3d8",
        color: "#8a5a00",
        ...shorthands.border("1px", "solid", "#f0dca0"),
        whiteSpace: "nowrap",
    },
    badgeBearbeiter: {
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.padding("1px", "8px"),
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 700,
        backgroundColor: "#efe3f2",
        color: "#4d2051",
        ...shorthands.border("1px", "solid", "#d9c4de"),
        whiteSpace: "nowrap",
    },
    badgeViaCompany: {
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        maxWidth: "220px",
        ...shorthands.padding("1px", "8px"),
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 600,
        backgroundColor: "#eef0f4",
        color: "#4a5568",
        ...shorthands.border("1px", "solid", "#d7dce4"),
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    meta: {
        fontSize: "12px",
        color: "#6f6c75",
    },
    metaStrong: {
        fontSize: "12px",
        color: "#3a163d",
        fontWeight: 600,
    },
    beteiligte: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        columnGap: "2px",
    },
    avatar: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        backgroundColor: "#ece7f0",
        color: "#4d2051",
        fontSize: "10px",
        fontWeight: 700,
        marginLeft: "-6px",
        ...shorthands.border("2px", "solid", "#ffffff"),
        boxSizing: "border-box",
    },
    avatarMe: {
        backgroundColor: "#4d2051",
        color: "#ffffff",
        ...shorthands.border("2px", "solid", "#c99700"),
    },
    avatarMore: {
        backgroundColor: "#f2eef4",
        color: "#6f6c75",
    },
    empty: {
        ...shorthands.padding("32px"),
        textAlign: "center",
        color: "#6f6c75",
        fontStyle: "italic",
        fontSize: "13px",
    },
    error: {
        ...shorthands.padding("20px"),
        textAlign: "center",
        color: "#b91c1c",
    },
    todoEmpty: {
        ...shorthands.padding("12px", "44px"),
        fontStyle: "italic",
        color: "#6f6c75",
        fontSize: "12px",
    },
});

export const ContactVorgaengeTab = forwardRef<ContactVorgaengeHandle, Props>(function ContactVorgaengeTab(
    { contactId, variant = "vorgaenge", onCountChange },
    ref,
) {
    const eingebettet = variant === "embedded";
    const styles = useStyles();
    const { openWindow } = useWindowManager();
    const { user } = useAuth();
    const currentUserId: string | null = user?.id ?? null;
    const { users, label } = useUserLabels();
    const [vorgaenge, setVorgaenge] = useState<VorgangDto[]>([]);
    const [standaloneTodos, setStandaloneTodos] = useState<TodoDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [contactLabel, setContactLabel] = useState<string>(`Kontakt #${contactId}`);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(apiUrl(`contact/${contactId}`));
                if (!res.ok) return;
                const c = await res.json();
                if (cancelled) return;
                const label =
                    c.displayName ||
                    [c.lastname, c.firstName].filter(Boolean).join(", ") ||
                    c.companyName ||
                    `Kontakt #${contactId}`;
                setContactLabel(label);
            } catch {
                /* fallback already set */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [contactId]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Per-Kontakt laden (SP1) statt mandantenweit ALLE Vorgänge/Todos zu
            // ziehen und clientseitig zu filtern: Vorgänge über
            // GET /vorgang?contactId= (Filter kunde.id), KUNDE-verknüpfte Todos
            // über GET /todo/by-linked/KUNDE/:id. Beide Endpoints prüfen bereits
            // die per-Kontakt-RBAC (fremder Kontakt → 403).
            const [contactVorgaenge, linkedTodos] = await Promise.all([
                vorgangService.getVorgaengeByContact(contactId),
                vorgangService.getTodosByLinked("KUNDE", contactId),
            ]);

            // Todos, die bereits als Sub-Todo eines geladenen Vorgangs erscheinen,
            // nicht zusätzlich in der „Weitere Vorgänge"-Liste doppeln.
            const nestedTodoIds = new Set<number>(
                contactVorgaenge.flatMap((v) => (v.todos ?? []).map((t) => t.id)),
            );
            const relevant = linkedTodos.filter((t) => !nestedTodoIds.has(t.id));

            // PO 02.08.2026: „Offene und Erledigte Vorgänge anzeigen, nach
            // offenen die Erledigten." Innerhalb der Blöcke bleibt die
            // Server-Reihenfolge (neueste zuerst) erhalten.
            const offenZuerst = [...contactVorgaenge].sort(
                (a, b) =>
                    Number(deriveVorgangStatus(a) === "Erledigt") -
                    Number(deriveVorgangStatus(b) === "Erledigt"),
            );
            setVorgaenge(offenZuerst);
            setStandaloneTodos(
                [...relevant].sort(
                    (a, b) => Number(a.status === "Erledigt") - Number(b.status === "Erledigt"),
                ),
            );
        } catch (err: any) {
            setError(err?.message ?? "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId]);

    useEffect(() => {
        load();
    }, [load]);

    const toggleExpand = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleStatusChange = async (todo: TodoDto, next: TodoStatusValue) => {
        try {
            await vorgangService.updateTodoStatus(todo.id, next);
            await load();
        } catch (err) {
            console.error("Status update failed", err);
        }
    };

    const hasContent = vorgaenge.length > 0 || standaloneTodos.length > 0;
    // „Vorgänge (N)": Vorgänge inkl. der als Vorgang gezählten standalone-Todos.
    const totalCount = vorgaenge.length + standaloneTodos.length;

    const openCreate = useCallback(() => {
        openVorgangCreateFlow(openWindow, {
            initialContactId: contactId,
            initialContactName: contactLabel,
        });
    }, [openWindow, contactId, contactLabel]);

    useImperativeHandle(ref, () => ({ reload: load, openCreate }), [load, openCreate]);

    // Zähler nach außen melden — erst nach dem Laden, sonst blinkt im
    // Sektionskopf kurz eine 0 auf.
    useEffect(() => {
        if (!loading) onCountChange?.(totalCount);
    }, [loading, totalCount, onCountChange]);

    const renderBeteiligte = (v: VorgangDto): React.ReactNode => {
        const ids = participantIds(v);
        if (ids.length === 0) {
            return <span className={styles.meta}>—</span>;
        }
        const shown = ids.slice(0, MAX_AVATARS);
        const rest = ids.length - shown.length;
        return (
            <div className={styles.beteiligte}>
                {shown.map((id) => {
                    const u = findUserBy(users, id);
                    const isMe = !!currentUserId && id === currentUserId;
                    return (
                        <span
                            key={id}
                            className={mergeClasses(styles.avatar, isMe && styles.avatarMe)}
                            title={isMe ? `${label(id)} (Du)` : label(id)}
                        >
                            {initialsFor(u, id)}
                        </span>
                    );
                })}
                {rest > 0 && (
                    <span
                        className={mergeClasses(styles.avatar, styles.avatarMore)}
                        title={ids.slice(MAX_AVATARS).map((id) => label(id)).join(", ")}
                    >
                        +{rest}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className={mergeClasses(styles.root, eingebettet && styles.rootEmbedded)}>
            {/* Eingebettet trägt die umgebende Klapp-Sektion Titel, Zähler und
                Aktionen — sonst stünde „Vorgänge" doppelt übereinander. */}
            {!eingebettet && (
                <div className={styles.header}>
                    <div>
                        <h3 className={styles.title}>
                            Vorgänge
                            <span className={styles.countBadge}>{totalCount}</span>
                        </h3>
                    </div>
                    <div className={styles.actionsRow}>
                        <button type="button" className={styles.iconBtn} onClick={load} title="Aktualisieren">
                            ↻
                        </button>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={openCreate}
                        >
                            + Neuer Vorgang
                        </button>
                    </div>
                </div>
            )}

            <div className={mergeClasses(styles.body, eingebettet && styles.bodyEmbedded)}>
                {loading && (
                    <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                        <Spinner size="tiny" label="Vorgänge werden geladen..." />
                    </div>
                )}

                {error && (
                    <div className={styles.error}>
                        <p>{error}</p>
                        <button type="button" className={styles.iconBtn} onClick={load}>
                            Erneut versuchen
                        </button>
                    </div>
                )}

                {vorgaenge.map((v) => {
                    const isOpen = expanded.has(v.id);
                    const todosOf = v.todos ?? [];
                    const istBearbeiter =
                        !!currentUserId && bearbeiterKeys(v.bearbeiter).includes(currentUserId);
                    const viaLabel = viaCompanyLabel(v);
                    return (
                        <div key={v.id} className={styles.section}>
                            <div
                                className={styles.vorgangRow}
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/vorgaenge/${v.id}`)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        navigate(`/vorgaenge/${v.id}`);
                                    }
                                }}
                            >
                                <button
                                    type="button"
                                    className={styles.chevronBtn}
                                    aria-label={isOpen ? "Aufgaben einklappen" : "Aufgaben ausklappen"}
                                    aria-expanded={isOpen}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(v.id);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                                    }}
                                >
                                    {isOpen ? "▾" : "▸"}
                                </button>
                                <div className={styles.main}>
                                    <div className={styles.titleLine}>
                                        <span className={styles.titelText} title={v.titel}>
                                            {v.titel}
                                        </span>
                                        {v.mentionedMe && (
                                            <span className={styles.badgeMention} title="Du wurdest in einer Notiz erwähnt">
                                                @ erwähnt
                                            </span>
                                        )}
                                        {istBearbeiter && (
                                            <span className={styles.badgeBearbeiter} title="Du bist Bearbeiter dieses Vorgangs">
                                                Ich bearbeite
                                            </span>
                                        )}
                                        {viaLabel && (
                                            <span className={styles.badgeViaCompany} title={`Vorgang der verbundenen Firma — ${viaLabel}`}>
                                                {viaLabel}
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.subLine}>
                                        {v.fortschrittErledigt}/{v.fortschrittGesamt} erledigt
                                        {todosOf.length > 0 && ` · ${todosOf.length} Aufgaben`}
                                    </div>
                                </div>
                                <span className={styles.metaStrong} title="Terminierte Wiedervorlage">
                                    TWV: {fmtTwv(v)}
                                </span>
                                <span className={styles.meta}>Frist: {fmtDate(v.frist)}</span>
                                {renderBeteiligte(v)}
                            </div>
                            {isOpen && (
                                <div>
                                    {todosOf.length === 0 ? (
                                        <div className={styles.todoEmpty}>
                                            Keine Aufgaben in diesem Vorgang.
                                        </div>
                                    ) : (
                                        todosOf.map((t) => (
                                            <TodoListRow
                                                key={t.id}
                                                todo={t}
                                                // To-dos haben keine eigene Detailseite mehr: die
                                                // Zeile öffnet den Eltern-Vorgang `v`, dessen
                                                // Todos-Karte die Detailansicht enthält. `v.id` ist
                                                // hier sicher — diese To-dos stammen aus `v.todos`
                                                // (das Todo-DTO selbst trägt keine `vorgangId`).
                                                onOpen={() => navigate(`/vorgaenge/${v.id}`)}
                                                onStatusChange={handleStatusChange}
                                            />
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* „Weitere Vorgänge" = KUNDE-verlinkte To-dos, die unter KEINEM der
                    oben geladenen Vorgänge hängen. Sie sind NICHT elternlos — ihr
                    Vorgang gehört nur einem anderen Kunden bzw. wurde für diesen
                    Kontakt nicht geladen. Bewusste Entscheidung: Diese Zeilen
                    navigieren NICHT. Ein Sprung auf den Eltern-Vorgang würde aus der
                    Kontaktakte herausführen — möglicherweise in die Akte eines
                    FREMDEN Kunden (Orientierungsverlust, bei fehlender Sichtbarkeit
                    zusätzlich 403/404). Die Zeile bleibt Anzeige; der Status lässt
                    sich über das Dropdown weiterhin ändern. */}
                {standaloneTodos.length > 0 && (
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>Weitere Vorgänge</div>
                        {standaloneTodos.map((t) => (
                            <TodoListRow key={t.id} todo={t} onStatusChange={handleStatusChange} />
                        ))}
                    </div>
                )}

                {!loading && !error && !hasContent && (
                    <div className={styles.empty}>
                        <p>Keine Vorgänge für diesen Kontakt gefunden.</p>
                        <button type="button" className={styles.primaryBtn} onClick={openCreate}>
                            + Neuer Vorgang
                        </button>
                    </div>
                )}
            </div>

        </div>
    );
});
